import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved, selectSelectedParishId } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { updateTableState } from 'redux/slices/common/appSlice';
import { RootState } from 'redux/store';
import * as groupsApi from 'services/groupsApi';
import { AnnouncementGroup } from 'types/announcements';
import type { CreateGroupPayload, UpdateGroupPayload } from 'types/announcementsV1';
import { sortByField } from 'utils/helpers';

interface AnnouncementGroupsState {
    records: AnnouncementGroup[];
    loading: boolean;
    error: string | null;
    total: number;
    inflightParishId: string | null;
    /** requestId of the latest dispatched table-page fetch — drops an older request's response
     *  that resolves after a newer one (e.g. rapid Next-Page clicks), matching the guard
     *  pattern used by parishOrganizationSlice / parishMemberSlice. */
    requestId: string | null;
    // The "To" picker in Send Announcement (AnnouncementGroupAutocomplete) has no pagination UI
    // of its own and needs the full list in one shot — kept separate from the table's
    // page-scoped `records` so the two consumers (one paginated, one not) don't stomp on each
    // other by sharing state driven by the table's page/pageSize in state.app.table.
    pickerRecords: AnnouncementGroup[];
    pickerLoading: boolean;
    pickerInflightParishId: string | null;
}

const initialState: AnnouncementGroupsState = {
    records: [],
    loading: false,
    error: null,
    total: 0,
    inflightParishId: null,
    requestId: null,
    pickerRecords: [],
    pickerLoading: false,
    pickerInflightParishId: null,
};

export type CreateAnnouncementGroupResult = {
    group: AnnouncementGroup;
    parishId: string;
    /** True when metadata POST succeeded but optional image PUT failed. */
    imageFailed: boolean;
};

export const createAnnouncementGroup = createAsyncThunkWrapper<
    CreateAnnouncementGroupResult | null,
    { parishId: string; payload: CreateGroupPayload; image?: Blob | null }
>(
    'createAnnouncementGroup',
    async ({ parishId, payload, image }, { getState }) => {
        if (!parishId) return null;
        let group: AnnouncementGroup;
        try {
            group = await groupsApi.createGroup(parishId, payload);
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }

        const currentAfterCreate = selectSelectedParishId(getState() as RootState);
        if (currentAfterCreate !== parishId) return null;

        if (!(image instanceof Blob)) {
            notify('Announcement group created successfully!', 'success');
            return { group, parishId, imageFailed: false };
        }

        try {
            group = await groupsApi.uploadGroupImage(parishId, group.id, image);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Announcement group created successfully!', 'success');
            return { group, parishId, imageFailed: false };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            // Group already exists — fulfill with group id so UI can retry image only (no re-POST).
            notify(getProblemDetail(err) || 'Group was created, but image upload failed. Retry the image upload.', 'error');
            return { group, parishId, imageFailed: true };
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to create group' }
);

/** Retry PUT image for an already-created group (H2). Does not POST metadata. */
export const uploadAnnouncementGroupImage = createAsyncThunkWrapper<
    { group: AnnouncementGroup; parishId: string } | null,
    { parishId: string; groupId: string; image: Blob }
>(
    'uploadAnnouncementGroupImage',
    async ({ parishId, groupId, image }, { getState }) => {
        if (!parishId || !groupId || !(image instanceof Blob)) return null;
        try {
            const group = await groupsApi.uploadGroupImage(parishId, groupId, image);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Group image uploaded successfully!', 'success');
            return { group, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to upload group image' }
);

export const updateAnnouncementGroup = createAsyncThunkWrapper<
    { group: AnnouncementGroup; parishId: string } | null,
    { parishId: string; groupId: string; payload: UpdateGroupPayload; image?: Blob | null }
>(
    'updateAnnouncementGroup',
    async ({ parishId, groupId, payload, image }, { getState }) => {
        if (!parishId || !groupId) return null;
        try {
            let group = await groupsApi.updateGroup(parishId, groupId, payload);
            if (image instanceof Blob) {
                group = await groupsApi.uploadGroupImage(parishId, groupId, image);
            }
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Group updated successfully', 'success');
            return { group, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to update group' }
);

export const fetchAnnouncementGroups = createAsyncThunkWrapper<
    { items: AnnouncementGroup[]; total: number; parishId: string } | null,
    { parishId: string }
>(
    'fetchAnnouncementGroups',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        try {
            // Server-paginated: read page/pageSize fresh from the shared table slice so the
            // request matches "Rows per page", and Announcements.tsx's fetch effect re-fires
            // on pageNumber changes too — Next/Prev now fetches the next page for real.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            const res = await groupsApi.listGroups(parishId, { page: pageNumber + 1, pageSize });
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load groups' }
);

/** Full, unpaginated batch for pickers with no pagination UI of their own (e.g. the "To" field
 *  in Send Announcement's AnnouncementGroupAutocomplete) — always the same fixed page 1/100
 *  request regardless of the table's current page/pageSize in state.app.table, so a picker
 *  can't end up showing an arbitrary page's worth of groups depending on what the table was
 *  last scrolled to (state.app.table is shared, global, and persisted). */
export const fetchAllAnnouncementGroups = createAsyncThunkWrapper<
    { items: AnnouncementGroup[]; total: number; parishId: string } | null,
    { parishId: string }
>(
    'fetchAllAnnouncementGroups',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        try {
            const res = await groupsApi.listGroups(parishId, { page: 1, pageSize: 100 });
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load groups' }
);

export const duplicateGroupById = createAsyncThunkWrapper<
    { group: AnnouncementGroup; parishId: string } | null,
    { parishId: string; id: string }
>(
    'announcementGroup/duplicateGroupById',
    async ({ parishId, id }, { getState }) => {
        if (!parishId || !id) return null;
        try {
            const group = await groupsApi.duplicateGroup(parishId, id);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Group duplicated successfully', 'success');
            return { group, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to duplicate group' }
);

export const deleteGroupById = createAsyncThunkWrapper<{ ok: true; parishId: string } | null, { parishId: string; id: string }>(
    'announcementGroup/deleteGroupById',
    async ({ parishId, id }, { dispatch, getState }) => {
        if (!parishId || !id) return null;
        try {
            await groupsApi.deleteGroup(parishId, id);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Group deleted successfully', 'success');
            // Deleting the only/last group on the current (server-paginated) page would
            // otherwise re-request that same now-out-of-range page below. Reset first — the
            // refetch's thunk reads pageNumber/pageSize fresh via getState(), so this dispatch
            // (synchronous) is already reflected by the time it runs, with no extra request.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            if (pageNumber !== 0) {
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }
            dispatch(fetchAnnouncementGroups({ parishId }));
            return { ok: true as const, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to delete group' }
);

const announcementGroupSlice = createSlice({
    name: 'announcementGroups',
    initialState,
    reducers: {
        clearAnnouncementGroups(state) {
            state.records = [];
            state.total = 0;
            state.inflightParishId = null;
            state.requestId = null;
            state.error = null;
            state.pickerRecords = [];
            state.pickerLoading = false;
            state.pickerInflightParishId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAnnouncementGroups.pending, (state, action) => {
            state.loading = true;
            // Only blank the list on a parish switch — a same-parish refetch (e.g. Next/Prev,
            // or after a delete) keeps showing the current page's rows under the loading
            // overlay instead of flashing empty then repainting.
            if (state.inflightParishId !== action.meta.arg.parishId) {
                state.records = [];
            }
            state.requestId = action.meta.requestId;
            state.inflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(fetchAnnouncementGroups.fulfilled, (state, action) => {
            // Drop a response for a request that's since been superseded (e.g. a second
            // Next-Page click fired before the first request resolved) so a slower, older
            // response can't overwrite newer data.
            if (action.meta.requestId !== state.requestId) return;
            state.loading = false;
            if (!action.payload) return;
            // No client-side sort here — this is one server page. Sorting it alone would break
            // global alphabetical order across pages (page 1 could end at "Z", page 2 start at
            // "A") unless the backend also orders by title; ordering is left to the server.
            state.records = action.payload.items || [];
            state.total = action.payload.total;
        });
        builder.addCase(fetchAnnouncementGroups.rejected, (state, action) => {
            if (action.meta.requestId !== state.requestId) return;
            state.loading = false;
            state.error = action.error.message || 'Failed to load groups';
        });

        builder.addCase(fetchAllAnnouncementGroups.pending, (state, action) => {
            state.pickerLoading = true;
            state.pickerInflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(fetchAllAnnouncementGroups.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.pickerInflightParishId === action.meta.arg.parishId) {
                    state.pickerLoading = false;
                }
                return;
            }
            if (action.payload.parishId !== state.pickerInflightParishId) return;
            state.pickerLoading = false;
            // This IS the full batch (page 1 of up to 100) — sorting it is globally correct.
            state.pickerRecords = sortByField(action.payload.items, 'title', 'ASC') || [];
        });
        builder.addCase(fetchAllAnnouncementGroups.rejected, (state, action) => {
            if (state.pickerInflightParishId === action.meta.arg.parishId) {
                state.pickerLoading = false;
            }
        });
    },
});

export const { clearAnnouncementGroups } = announcementGroupSlice.actions;
export default announcementGroupSlice.reducer;
